import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Heart, User, LogOut, ChevronRight, XCircle, MapPin, Plus, Edit, Trash2, X, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/5',
  processing: 'text-blue-400 border-blue-400/30 bg-blue-400/5',
  shipped: 'text-primary border-primary/30 bg-primary/5',
  delivered: 'text-green-400 border-green-400/30 bg-green-400/5',
  cancelled: 'text-red-400 border-red-400/30 bg-red-400/5',
};

const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

const TABS = [
  { id: 'orders',    label: 'My Orders',  icon: Package  },
  { id: 'wishlist',  label: 'Wishlist',   icon: Heart    },
  { id: 'addresses', label: 'Addresses',  icon: MapPin   },
  { id: 'profile',   label: 'Profile',    icon: User     },
];

const EMPTY_ADDR = { label: '', full_name: '', phone: '', street: '', area: '', city: '', emirate: 'Dubai', country: 'UAE', is_default: false };

export default function UserDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout, getHeaders, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'orders');
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [addrForm, setAddrForm] = useState(null); // null=closed, EMPTY_ADDR=new, {...}=edit
  const [addrSaving, setAddrSaving] = useState(false);

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this order? This cannot be undone.')) return;
    try {
      await axios.put(`${API}/orders/${orderId}/cancel`, {}, { headers: getHeaders() });
      toast.success('Order cancelled and stock restored');
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot cancel this order'); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    setProfileForm({ name: user.name || '', phone: user.phone || '' });
    loadData();
  }, [user, authLoading, activeTab]);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      if (activeTab === 'orders') {
        const res = await axios.get(`${API}/orders`, { headers: getHeaders() });
        setOrders(res.data);
      } else if (activeTab === 'wishlist') {
        const res = await axios.get(`${API}/wishlist`, { headers: getHeaders() });
        setWishlist(res.data.products || []);
      } else if (activeTab === 'addresses') {
        const res = await axios.get(`${API}/auth/addresses`, { headers: getHeaders() });
        setAddresses(res.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    const res = await axios.get(`${API}/orders`, { headers: getHeaders() });
    setOrders(res.data);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, profileForm, { headers: getHeaders() });
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setSavingProfile(false); }
  };

  const saveAddress = async () => {
    if (!addrForm.label || !addrForm.full_name || !addrForm.phone || !addrForm.street || !addrForm.area) {
      toast.error('Please fill all required fields'); return;
    }
    setAddrSaving(true);
    try {
      if (addrForm.id) {
        await axios.put(`${API}/auth/addresses/${addrForm.id}`, addrForm, { headers: getHeaders() });
        toast.success('Address updated');
      } else {
        await axios.post(`${API}/auth/addresses`, addrForm, { headers: getHeaders() });
        toast.success('Address saved');
      }
      setAddrForm(null);
      const res = await axios.get(`${API}/auth/addresses`, { headers: getHeaders() });
      setAddresses(res.data || []);
    } catch { toast.error('Failed to save address'); }
    finally { setAddrSaving(false); }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/auth/addresses/${id}`, { headers: getHeaders() });
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Address deleted');
    } catch { toast.error('Failed to delete address'); }
  };

  if (!user) return null;

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        <div className="bg-surface border-b border-white/5 py-5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 border border-primary/30 flex items-center justify-center font-bold text-primary text-lg">
                {user.name?.[0]}
              </div>
              <div>
                <h1 className="font-heading font-black text-xl uppercase text-white">Welcome, {user.name?.split(' ')[0]}</h1>
                <p className="text-muted-foreground text-xs">{user.email}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
          <div className="flex gap-8">
            {/* Sidebar */}
            <aside className="hidden md:block w-48 flex-shrink-0">
              <div className="bg-surface border border-white/5 overflow-hidden">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setActiveTab(id)}
                    className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm transition-colors border-b border-white/5 last:border-0 ${activeTab === id ? 'bg-primary/10 text-primary border-l-2 border-l-primary' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                    data-testid={`dashboard-tab-${id}`}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
                <button onClick={() => { logout(); navigate('/'); }}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-400 hover:bg-white/5 transition-colors"
                  data-testid="dashboard-logout-btn">
                  <LogOut size={14} /> Sign Out
                </button>
              </div>
            </aside>

            {/* Mobile tabs */}
            <div className="md:hidden w-full">
              <div className="flex border-b border-white/10 mb-4 overflow-x-auto">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${activeTab === id ? 'text-primary border-b-2 border-primary' : 'text-white/50 hover:text-white'}`}>
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">

              {/* Orders tab */}
              {activeTab === 'orders' && (
                <div data-testid="orders-tab">
                  <h2 className="font-heading font-bold text-xl uppercase text-white mb-4">My Orders</h2>
                  {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 shimmer-bg" />)}</div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12 border border-white/5">
                      <Package size={40} className="text-white/20 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm mb-4">No orders yet</p>
                      <button onClick={() => navigate('/products')} className="btn-primary text-sm py-2 px-6">Start Shopping</button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orders.map(order => (
                        <div key={order.id} className="bg-surface border border-white/5 p-4 hover:border-white/15 transition-colors" data-testid={`order-${order.id}`}>
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                              <p className="text-sm font-bold text-white font-mono">{order.order_number}</p>
                              <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-AE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            </div>
                            <div className="text-right">
                              <p className="price-tag text-primary font-black text-base">AED {order.total?.toFixed(0)}</p>
                              <span className={`inline-block text-xs px-2 py-0.5 border font-bold capitalize ${STATUS_COLORS[order.status] || 'text-white border-white/20'}`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 overflow-x-auto">
                            {order.items?.slice(0, 4).map((item, i) => (
                              <div key={i} className="flex-shrink-0">
                                <img src={item.image} alt={item.product_name} className="w-12 h-12 object-cover border border-white/5" />
                              </div>
                            ))}
                            {order.items?.length > 4 && (
                              <div className="w-12 h-12 border border-white/5 flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">+{order.items.length - 4}</div>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-muted-foreground">{order.items?.length} item{order.items?.length !== 1 ? 's' : ''} · {order.payment_method?.toUpperCase()}</p>
                            <div className="flex items-center gap-3">
                              {['pending', 'processing'].includes(order.status) && (
                                <button onClick={() => cancelOrder(order.id)}
                                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                                  data-testid={`cancel-order-${order.id}`}>
                                  <XCircle size={12} /> Cancel
                                </button>
                              )}
                              <button onClick={() => navigate(`/order-success/${order.id}`)}
                                className="text-xs text-primary hover:text-white flex items-center gap-1 transition-colors">
                                View Details <ChevronRight size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Wishlist tab */}
              {activeTab === 'wishlist' && (
                <div data-testid="wishlist-tab">
                  <h2 className="font-heading font-bold text-xl uppercase text-white mb-4">My Wishlist</h2>
                  {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="aspect-square shimmer-bg" />)}</div>
                  ) : wishlist.length === 0 ? (
                    <div className="text-center py-12 border border-white/5">
                      <Heart size={40} className="text-white/20 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm mb-4">Your wishlist is empty</p>
                      <button onClick={() => navigate('/products')} className="btn-primary text-sm py-2 px-6">Browse Products</button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {wishlist.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses tab */}
              {activeTab === 'addresses' && (
                <div data-testid="addresses-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-bold text-xl uppercase text-white">Saved Addresses</h2>
                    {!addrForm && (
                      <button onClick={() => setAddrForm({ ...EMPTY_ADDR })}
                        className="flex items-center gap-1.5 bg-primary text-black font-bold text-xs uppercase tracking-wider px-4 py-2 hover:bg-white transition-colors"
                        data-testid="add-address-btn">
                        <Plus size={13} /> Add Address
                      </button>
                    )}
                  </div>

                  {/* Address form */}
                  {addrForm && (
                    <div className="bg-surface border border-primary/20 p-5 mb-5" data-testid="address-form">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-primary">
                          {addrForm.id ? 'Edit Address' : 'New Address'}
                        </h3>
                        <button onClick={() => setAddrForm(null)} className="text-white/40 hover:text-white transition-colors"><X size={16} /></button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Label (e.g. Home, Office) *</label>
                          <input value={addrForm.label} onChange={e => setAddrForm(f => ({ ...f, label: e.target.value }))}
                            placeholder="Home" className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-label-input" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Full Name *</label>
                          <input value={addrForm.full_name} onChange={e => setAddrForm(f => ({ ...f, full_name: e.target.value }))}
                            className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-name-input" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Phone *</label>
                          <input value={addrForm.phone} onChange={e => setAddrForm(f => ({ ...f, phone: e.target.value }))}
                            placeholder="+971 50 XXX XXXX" className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-phone-input" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Street Address *</label>
                          <input value={addrForm.street} onChange={e => setAddrForm(f => ({ ...f, street: e.target.value }))}
                            className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-street-input" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Area *</label>
                          <input value={addrForm.area} onChange={e => setAddrForm(f => ({ ...f, area: e.target.value }))}
                            className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-area-input" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">City</label>
                          <input value={addrForm.city} onChange={e => setAddrForm(f => ({ ...f, city: e.target.value }))}
                            className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-city-input" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Emirate</label>
                          <select value={addrForm.emirate} onChange={e => setAddrForm(f => ({ ...f, emirate: e.target.value }))}
                            className="w-full bg-background border border-white/10 focus:border-primary text-white px-4 py-2.5 outline-none text-sm"
                            style={{ borderRadius: 0 }} data-testid="addr-emirate-select">
                            {EMIRATES.map(em => <option key={em} value={em}>{em}</option>)}
                          </select>
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2">
                          <input type="checkbox" id="default-chk" checked={addrForm.is_default}
                            onChange={e => setAddrForm(f => ({ ...f, is_default: e.target.checked }))}
                            className="accent-primary" data-testid="addr-default-checkbox" />
                          <label htmlFor="default-chk" className="text-xs text-white/60 cursor-pointer">Set as default address</label>
                        </div>
                      </div>
                      <div className="mt-4 flex gap-3">
                        <button onClick={saveAddress} disabled={addrSaving}
                          className="flex items-center gap-1.5 bg-primary text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-50"
                          data-testid="save-address-btn">
                          <Check size={13} /> {addrSaving ? 'Saving...' : 'Save Address'}
                        </button>
                        <button onClick={() => setAddrForm(null)} className="text-xs text-white/40 hover:text-white border border-white/10 px-4 py-2.5 transition-colors">Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Address list */}
                  {loading ? (
                    <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-24 shimmer-bg" />)}</div>
                  ) : addresses.length === 0 && !addrForm ? (
                    <div className="text-center py-12 border border-white/5">
                      <MapPin size={36} className="text-white/20 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm mb-4">No saved addresses yet</p>
                      <button onClick={() => setAddrForm({ ...EMPTY_ADDR })}
                        className="btn-primary text-sm py-2 px-6" data-testid="add-first-address-btn">
                        Add Your First Address
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {addresses.map(addr => (
                        <div key={addr.id} className="bg-surface border border-white/5 p-4 hover:border-white/15 transition-colors" data-testid={`address-card-${addr.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin size={14} className="text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-sm font-bold text-primary uppercase tracking-wider">{addr.label}</span>
                              {addr.is_default && (
                                <span className="text-xs border border-primary/30 text-primary/70 px-1.5 py-0.5">Default</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => setAddrForm({ ...addr })}
                                className="text-white/40 hover:text-primary transition-colors"
                                data-testid={`edit-address-${addr.id}`} title="Edit">
                                <Edit size={13} />
                              </button>
                              <button onClick={() => deleteAddress(addr.id)}
                                className="text-white/40 hover:text-red-400 transition-colors"
                                data-testid={`delete-address-${addr.id}`} title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-white font-semibold">{addr.full_name}</p>
                          <p className="text-xs text-white/60 mt-0.5">{addr.street}, {addr.area}</p>
                          <p className="text-xs text-white/60">{addr.emirate}, UAE &nbsp;·&nbsp; {addr.phone}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Profile tab */}
              {activeTab === 'profile' && (
                <div data-testid="profile-tab">
                  <h2 className="font-heading font-bold text-xl uppercase text-white mb-4">My Profile</h2>
                  <div className="bg-surface border border-white/5 p-6 max-w-md">
                    <form onSubmit={saveProfile} className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Full Name</label>
                        <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                          className="w-full bg-background border border-white/10 focus:border-primary text-white px-4 py-3 outline-none text-sm transition-colors"
                          style={{ borderRadius: 0 }} data-testid="profile-name-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Email</label>
                        <input value={user.email} readOnly
                          className="w-full bg-background border border-white/5 text-white/40 px-4 py-3 outline-none text-sm"
                          style={{ borderRadius: 0 }} />
                        <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Phone</label>
                        <input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                          placeholder="+971 50 XXX XXXX"
                          className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                          style={{ borderRadius: 0 }} data-testid="profile-phone-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Account Type</label>
                        <div className="bg-background border border-white/5 px-4 py-3">
                          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 ${user.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-white/10 text-white/60'}`}>
                            {user.role}
                          </span>
                        </div>
                      </div>
                      <button type="submit" disabled={savingProfile}
                        className="bg-primary text-black font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-white transition-colors disabled:opacity-60"
                        data-testid="save-profile-btn">
                        {savingProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
